import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  TextField, 
  Button, 
  Grid, 
  Divider, 
  IconButton, 
  Menu, 
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Chip,
  Tabs,
  Tab,
  Card,
  CardContent,
  Stack,
  Fab
} from '@mui/material';
import { 
  Save as SaveIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  MoreVert as MoreIcon,
  AutoAwesome as AIIcon,
  Warning as WarningIcon,
  Lightbulb as TipIcon,
  FormatQuote as QuoteIcon,
  Translate as TranslateIcon,
  Summarize as SummarizeIcon,
  Check as CheckIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Description as DescriptionIcon,
  Search as SearchIcon,
  FindReplace as FindReplaceIcon,
  TextFields as TextFieldsIcon,
  Psychology as PsychologyIcon,
  Print as PrintIcon,
  Close as CloseIcon,
  AutoAwesome as AutoAwesomeIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  KeyboardArrowUp,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import ScrollToTop from '../common/ScrollToTop';

interface DocumentSection {
  id: number;
  title: string;
  content: string;
}

interface DocumentEditorComponentProps {
  document: {
    title: string;
    sections: DocumentSection[];
    activeSection: number | null;
  };
  onSave: (document: any) => void;
  onTitleChange?: (title: string) => void;
  onSectionChange: (index: number) => void;
  onSectionContentChange: (content: string) => void;
  onSectionTitleChange: (index: number, title: string) => void;
  onAddSection: () => void;
  onRemoveSection: (index: number) => void;
  onAIRequest?: (prompt: string, context: string) => Promise<string>;
  onPrint?: () => void;
}

const DocumentEditorComponent: React.FC<DocumentEditorComponentProps> = ({
  document,
  onSave,
  onTitleChange,
  onSectionChange,
  onSectionContentChange,
  onSectionTitleChange,
  onAddSection,
  onRemoveSection,
  onAIRequest,
  onPrint
}) => {
  // Document state
  const [title, setTitle] = useState(document.title);
  const [sections, setSections] = useState(document.sections);
  const [activeSection, setActiveSection] = useState<number | null>(0);
  const [activeTab, setActiveTab] = useState(0);
  const [saving, setSaving] = useState(false);
  
  // AI features state
  const [aiDrawerOpen, setAIDrawerOpen] = useState(false);
  const [aiPrompt, setAIPrompt] = useState('');
  const [aiLoading, setAILoading] = useState(false);
  const [aiResult, setAIResult] = useState<string | null>(null);
  
  // Risk analysis state
  const [riskAnalysis, setRiskAnalysis] = useState<any[]>([]);
  
  // Mock risk analysis data for testing
  const mockRiskAnalysis = [
    {
      title: 'Ambiguous Termination Clause',
      content: 'The termination clause in section 8.2 is ambiguous and could be interpreted in multiple ways. The phrase "reasonable notice" is not clearly defined.',
      severity: 'high',
      suggestion: 'Define "reasonable notice" with specific timeframes (e.g., 30 days written notice) and clarify the acceptable methods of delivery for termination notices.',
      timestamp: new Date().toISOString()
    },
    {
      title: 'Missing Liability Cap',
      content: 'The contract does not specify a cap on liability, which could expose parties to unlimited financial risk in case of breach or damages.',
      severity: 'medium',
      suggestion: 'Add a liability limitation clause that caps damages at an appropriate amount, such as the total fees paid under the contract or a specific dollar amount.',
      timestamp: new Date().toISOString()
    },
    {
      title: 'Incomplete Payment Terms',
      content: 'Payment terms lack specificity regarding late payments. No interest rate or late fee structure is defined.',
      severity: 'medium',
      suggestion: 'Include specific late payment penalties (e.g., 1.5% monthly interest on overdue amounts) and define when a payment is considered late (e.g., 15 days after invoice date).',
      timestamp: new Date().toISOString()
    }
  ];
  
  // UI state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [snackbar, setSnackbar] = useState<{open: boolean, message: string, severity: 'success' | 'error' | 'info'}>({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // Add state for section context menu
  const [sectionMenuAnchorEl, setSectionMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [sectionMenuIndex, setSectionMenuIndex] = useState<number | null>(null);
  
  // Handle menu open/close
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };
  
  // Handle section selection
  const handleSectionSelect = (index: number) => {
    setActiveSection(index);
  };
  
  // Handle section content change
  const handleSectionContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (activeSection === null) return;
    
    const updatedSections = [...sections];
    updatedSections[activeSection].content = e.target.value;
    setSections(updatedSections);
  };
  
  // Handle section title change
  const handleSectionTitleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
    const updatedSections = [...sections];
    updatedSections[index].title = e.target.value;
    setSections(updatedSections);
  };
  
  // Handle add section
  const handleAddSection = () => {
    const newSection = {
      id: Date.now(), // Use timestamp as temporary ID
      title: `New Section ${sections.length + 1}`,
      content: ''
    };
    
    setSections([...sections, newSection]);
    setActiveSection(sections.length);
  };
  
  // Handle delete section
  const handleDeleteSection = (index: number) => {
    if (sections.length <= 1) {
      showSnackbar('Cannot delete the last section', 'error');
      return;
    }
    
    const updatedSections = [...sections];
    updatedSections.splice(index, 1);
    setSections(updatedSections);
    
    if (activeSection === index) {
      setActiveSection(Math.max(0, index - 1));
    } else if (activeSection !== null && activeSection > index) {
      setActiveSection(activeSection - 1);
    }
  };
  
  // Handle document save
  const handleSave = async () => {
    setSaving(true);
    
    try {
      const updatedDocument = {
        ...document,
        title,
        sections,
        updatedAt: new Date().toISOString()
      };
      
      await onSave(updatedDocument);
      showSnackbar('Document saved successfully', 'success');
    } catch (error) {
      console.error('Error saving document:', error);
      showSnackbar('Failed to save document', 'error');
    } finally {
      setSaving(false);
    }
  };
  
  // PDF notification banner
  const PdfNotificationBanner = () => {
    // Check if this document was converted from a PDF
    const isPdfDocument = document.title.toLowerCase().endsWith('.pdf') || 
                         (document.sections.length > 0 && 
                          document.sections[0].content.startsWith('PDF Document:'));
    
    if (!isPdfDocument) return null;
    
    return (
      <Box sx={{ 
        mb: 3, 
        p: 2, 
        bgcolor: 'primary.light', 
        color: 'primary.contrastText',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center'
      }}>
        <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
          <DescriptionIcon fontSize="large" />
        </Box>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">
            PDF Document Converted to Text
          </Typography>
          <Typography variant="body2">
            This PDF has been converted to text format for editing. Some formatting may have been lost in the process.
            You can edit the content directly in the sections below.
          </Typography>
        </Box>
      </Box>
    );
  };
  
  // Handle print document
  const handlePrintDocument = () => {
    // Switch to preview tab first
    setActiveTab(1);
    
    // Use setTimeout to ensure the preview is rendered before printing
    setTimeout(() => {
      window.print();
    }, 300);
  };
  
  // Handle risk analysis
  const handleRunRiskAnalysis = () => {
    // Show loading indicator
    setAILoading(true);
    showSnackbar('Analyzing document for risks...', 'info');
    
    // Use mock data after a short delay for better UX
    setTimeout(() => {
      try {
        // Set the risk analysis result using mock data
        setRiskAnalysis(mockRiskAnalysis);
        
        // Show success message
        showSnackbar('Risk analysis completed', 'success');
      } catch (error) {
        console.error('Error in risk analysis:', error);
        showSnackbar('Failed to analyze document risks', 'error');
      } finally {
        setAILoading(false);
      }
    }, 1000); // 1 second delay for better UX
  };
  
  // Handle document download as PDF
  const handleDownloadPDF = () => {
    // Switch to preview tab first to ensure proper formatting
    setActiveTab(1);
    
    // Use setTimeout to ensure the preview is rendered before generating PDF
    setTimeout(() => {
      showSnackbar('Preparing document for download...', 'info');
      
      try {
        // Create a formatted HTML document with proper styling
        let documentContent = `<!DOCTYPE html>
<html>
<head>
  <title>${title}</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; color: #333; }
    h1 { text-align: center; color: #1a365d; }
    .date { text-align: center; color: #666; margin-bottom: 30px; }
    h2 { color: #1a365d; border-bottom: 1px solid #eee; padding-bottom: 5px; margin-top: 30px; }
    .content { margin-bottom: 20px; text-align: justify; }
    .footer { margin-top: 50px; border-top: 1px solid #eee; padding-top: 20px; font-size: 12px; color: #666; }
    @media print { body { margin: 0; } }
  </style>
</head>
<body>
  <h1>${title}</h1>
  <div class="date">Generated on: ${new Date().toLocaleDateString()}</div>
`;

        // Add each section
        sections.forEach(section => {
          // Process content to replace newlines with <br> tags
          const formattedContent = section.content.replace(/\n/g, '<br>');
          
          // Add section to HTML document
          documentContent += `  <h2>${section.title}</h2>
  <div class="content">${formattedContent}</div>
`;
        });

        // Add footer
        documentContent += `  <div class="footer">
    Generated on ${new Date().toLocaleDateString()}
  </div>
</body>
</html>`;

        // Create a blob with the HTML content
        const blob = new Blob([documentContent], { type: 'text/html' });
        
        // Create a download link
        const url = URL.createObjectURL(blob);
        const link = window.document.createElement('a');
        link.href = url;
        link.download = `${title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.html`;
        
        // Append to body, click and remove
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
        
        // Clean up the URL object
        setTimeout(() => URL.revokeObjectURL(url), 100);
        
        // Show instructions for printing to PDF
        showSnackbar('Document downloaded as HTML. Open it and use Print > Save as PDF for a PDF version.', 'success');
      } catch (error) {
        console.error('Error generating document:', error);
        showSnackbar('Failed to generate document', 'error');
      }
    }, 500);
  };
  
  // Handle document sharing
  const handleShareDocument = () => {
    // Switch to preview tab first to ensure proper formatting
    setActiveTab(1);
    
    // Use setTimeout to ensure the preview is rendered before sharing
    setTimeout(() => {
      showSnackbar('Preparing document for sharing...', 'info');
      
      try {
        // Prepare the full document content
        let fullDocumentContent = `${title}\n\nDate: ${new Date().toLocaleDateString()}\n\n`;
        
        // Add all sections with proper formatting
        sections.forEach(section => {
          fullDocumentContent += `${section.title}\n\n${section.content}\n\n`;
        });
        
        // Create a blob with the complete document content
        const blob = new Blob([fullDocumentContent], { type: 'text/plain' });
        const file = new File([blob], `${title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.txt`, { type: 'text/plain' });
        
        // Create a custom share dialog
        const shareDialog = window.document.createElement('div');
        shareDialog.style.position = 'fixed';
        shareDialog.style.top = '50%';
        shareDialog.style.left = '50%';
        shareDialog.style.transform = 'translate(-50%, -50%)';
        shareDialog.style.backgroundColor = 'white';
        shareDialog.style.padding = '20px';
        shareDialog.style.borderRadius = '8px';
        shareDialog.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
        shareDialog.style.zIndex = '9999';
        shareDialog.style.width = '300px';
        
        shareDialog.innerHTML = `
          <h3 style="margin-top: 0; color: #1a365d;">Share Document</h3>
          <p style="margin-bottom: 20px;">Choose how to share this document:</p>
          <div style="display: flex; flex-direction: column; gap: 10px;">
            <button id="share-email" style="padding: 10px; background-color: #f0f7ff; border: 1px solid #e1efff; border-radius: 4px; cursor: pointer;">
              <span style="font-weight: bold;">Email</span>
            </button>
            <button id="share-discord" style="padding: 10px; background-color: #f0f7ff; border: 1px solid #e1efff; border-radius: 4px; cursor: pointer;">
              <span style="font-weight: bold;">Discord</span>
            </button>
            <button id="share-download" style="padding: 10px; background-color: #f0f7ff; border: 1px solid #e1efff; border-radius: 4px; cursor: pointer;">
              <span style="font-weight: bold;">Download as Text</span>
            </button>
            <button id="share-cancel" style="padding: 10px; background-color: #f5f5f5; border: 1px solid #e0e0e0; border-radius: 4px; margin-top: 10px; cursor: pointer;">
              Cancel
            </button>
          </div>
        `;
        
        window.document.body.appendChild(shareDialog);
        
        // Add event listeners
        window.document.getElementById('share-email')?.addEventListener('click', () => {
          window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(fullDocumentContent)}`;
          window.document.body.removeChild(shareDialog);
          showSnackbar('Opening email client', 'success');
        });
        
        window.document.getElementById('share-discord')?.addEventListener('click', () => {
          // Create a download link for Discord
          const url = URL.createObjectURL(file);
          const link = window.document.createElement('a');
          link.href = url;
          link.download = file.name;
          link.click();
          URL.revokeObjectURL(url);
          
          // Open Discord in a new tab
          window.open('https://discord.com/channels/@me', '_blank');
          window.document.body.removeChild(shareDialog);
          showSnackbar('Document ready for Discord. Please upload the downloaded file.', 'success');
        });
        
        window.document.getElementById('share-download')?.addEventListener('click', () => {
          const url = URL.createObjectURL(file);
          const link = window.document.createElement('a');
          link.href = url;
          link.download = file.name;
          link.click();
          URL.revokeObjectURL(url);
          window.document.body.removeChild(shareDialog);
          showSnackbar('Document downloaded as text', 'success');
        });
        
        window.document.getElementById('share-cancel')?.addEventListener('click', () => {
          window.document.body.removeChild(shareDialog);
        });
        
      } catch (error) {
        console.error('Error preparing document for sharing:', error);
        showSnackbar('Failed to prepare document for sharing', 'error');
      }
    }, 500);
  };
  
  // Handle view history
  const handleViewHistory = () => {
    // Switch to history tab
    setActiveTab(2);
    handleMenuClose();
  };
  
  // Handle AI drawer toggle
  const handleAIDrawerToggle = () => {
    setAIDrawerOpen(!aiDrawerOpen);
    setAIResult(null);
    setAIPrompt('');
  };
  
  // Handle AI prompt submission
  const handleAIPromptSubmit = async () => {
    if (!aiPrompt.trim() || !onAIRequest) return;
    
    // Check if a section is selected
    if (activeSection === null) {
      showSnackbar('Please select a section first', 'error');
      return;
    }
    
    setAILoading(true);
    try {
      // Get the current section content as context
      const context = sections[activeSection].content;
      
      // If the section is empty, show a message but still allow the AI to generate content
      if (!context.trim()) {
        console.log('Section content is empty, AI will generate new content based on the prompt');
      }
      
      console.log('Sending AI request with prompt:', aiPrompt);
      console.log('Section content length:', context.length);
      
      // Call the AI API with the current section content as context
      const result = await onAIRequest(aiPrompt, context);
      console.log('Received AI response, length:', result.length);
      setAIResult(result);
    } catch (error) {
      console.error('Error getting AI response:', error);
      showSnackbar('Failed to get AI response', 'error');
    } finally {
      setAILoading(false);
    }
  };
  
  // Handle applying AI result to the current section
  const handleApplyAIResult = () => {
    if (activeSection === null || !aiResult) return;
    
    const updatedSections = [...sections];
    
    // Determine if we should replace or append the AI-generated content
    if (aiPrompt.toLowerCase().includes('improve') || 
        aiPrompt.toLowerCase().includes('enhance') || 
        aiPrompt.toLowerCase().includes('revise') || 
        aiPrompt.toLowerCase().includes('edit')) {
      // For improvement prompts, append the AI result with a clear marker
      updatedSections[activeSection].content += `

**AI Improvement (${new Date().toLocaleDateString()}): **
${aiResult}`;
      showSnackbar('AI improvements added to the section', 'success');
    } else {
      // For other prompts, replace the content entirely
      updatedSections[activeSection].content = aiResult;
      showSnackbar('Section content replaced with AI-generated text', 'success');
    }
    
    // Update the document and reset the AI state
    setSections(updatedSections);
    setAIDrawerOpen(false); // Close the drawer to show the changes
    setActiveTab(0); // Switch to editor tab to show changes
    setAIResult(null);
    setAIPrompt('');
  };
  
  // Show snackbar message
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };
  
  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };
  
  // Add this function to handle title auto resize
  const handleSectionTitleResize = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
    // Adjust the height of the section title input to fit the content
    if (e.target) {
      e.target.style.height = 'auto';
      e.target.style.height = `${e.target.scrollHeight}px`;
    }
  };
  
  // Handle section context menu open
  const handleSectionMenuOpen = (event: React.MouseEvent<HTMLElement>, index: number) => {
    event.preventDefault();
    event.stopPropagation();
    setSectionMenuAnchorEl(event.currentTarget);
    setSectionMenuIndex(index);
  };
  
  // Handle section context menu close
  const handleSectionMenuClose = () => {
    setSectionMenuAnchorEl(null);
    setSectionMenuIndex(null);
  };
  
  // Handle moving section up
  const handleMoveSectionUp = (index: number) => {
    if (index <= 0) return;
    const updatedSections = [...sections];
    const temp = updatedSections[index];
    updatedSections[index] = updatedSections[index - 1];
    updatedSections[index - 1] = temp;
    setSections(updatedSections);
    
    // Update active section if needed
    if (activeSection === index) {
      setActiveSection(index - 1);
    } else if (activeSection === index - 1) {
      setActiveSection(index);
    }
    
    handleSectionMenuClose();
  };
  
  // Handle moving section down
  const handleMoveSectionDown = (index: number) => {
    if (index >= sections.length - 1) return;
    const updatedSections = [...sections];
    const temp = updatedSections[index];
    updatedSections[index] = updatedSections[index + 1];
    updatedSections[index + 1] = temp;
    setSections(updatedSections);
    
    // Update active section if needed
    if (activeSection === index) {
      setActiveSection(index + 1);
    } else if (activeSection === index + 1) {
      setActiveSection(index);
    }
    
    handleSectionMenuClose();
  };
  
  // Handle renaming section
  const handleRenameSection = (index: number) => {
    setActiveSection(index);
    handleSectionMenuClose();
    
    // Focus on the section title input after a short delay
    setTimeout(() => {
      const sectionTitleInput = window.document.getElementById(`section-title-${index}`);
      if (sectionTitleInput) {
        sectionTitleInput.focus();
      }
    }, 100);
  };
  
  // Add this helper function to provide contextual editing tips based on section titles
  const getEditingTipForSection = (title: string): string => {
    const lowerTitle = title.toLowerCase();
    
    if (lowerTitle.includes('introduction') || lowerTitle.includes('preamble')) {
      return 'background information and the purpose of the document. Identify all parties involved.';
    } else if (lowerTitle.includes('scope') || lowerTitle.includes('services')) {
      return 'specific details about what is being provided, deliverables, and any limitations.';
    } else if (lowerTitle.includes('term') || lowerTitle.includes('duration')) {
      return 'the start date, end date, and any renewal provisions.';
    } else if (lowerTitle.includes('payment') || lowerTitle.includes('compensation') || lowerTitle.includes('fee')) {
      return 'payment amounts, schedules, methods, and any late payment terms.';
    } else if (lowerTitle.includes('confidential') || lowerTitle.includes('privacy')) {
      return 'what information is considered confidential and how it should be protected.';
    } else if (lowerTitle.includes('termination')) {
      return 'conditions under which the agreement can be ended and any notice requirements.';
    } else if (lowerTitle.includes('liability') || lowerTitle.includes('indemnification')) {
      return 'who is responsible for what risks and any limitations on liability.';
    } else if (lowerTitle.includes('warranty') || lowerTitle.includes('guarantee')) {
      return 'promises about the quality of services or products and remedies for failure.';
    } else if (lowerTitle.includes('intellectual property') || lowerTitle.includes('ip')) {
      return 'ownership of any created works, patents, trademarks, or other IP.';
    } else if (lowerTitle.includes('dispute') || lowerTitle.includes('arbitration')) {
      return 'how disagreements will be resolved, including jurisdiction and venue.';
    } else if (lowerTitle.includes('force majeure')) {
      return 'provisions for unforeseeable circumstances that prevent fulfillment of obligations.';
    } else if (lowerTitle.includes('notice')) {
      return 'how formal communications between parties should be delivered.';
    } else if (lowerTitle.includes('amendment') || lowerTitle.includes('modification')) {
      return 'how changes to the agreement can be made.';
    } else if (lowerTitle.includes('assignment')) {
      return 'whether rights or obligations can be transferred to other parties.';
    } else if (lowerTitle.includes('governing law')) {
      return 'which laws apply to the interpretation and enforcement of the agreement.';
    } else if (lowerTitle.includes('signature') || lowerTitle.includes('execution')) {
      return 'signature blocks for all parties, including dates and titles.';
    }
    
    // Default tip
    return 'clear, specific language that addresses the key points relevant to this section';
  };
  
  // Add this helper function to determine document type from title
  const getDocumentTypeFromTitle = (title: string): string => {
    // Common document types to check for
    const documentTypes = [
      'Contract', 'Agreement', 'Lease', 'NDA', 'Proposal', 'Memorandum', 'Letter', 
      'Policy', 'Terms', 'Conditions', 'Statement', 'Report', 'Plan', 'Certificate', 
      'Declaration', 'Invoice', 'Receipt', 'Quote', 'Estimate'
    ];
    
    // Check if the title contains any of the document types
    for (const type of documentTypes) {
      if (title.includes(type)) {
        // If we find a type, check for common prefixes to create a more descriptive label
        if (title.toLowerCase().includes('employment')) {
          return 'Employment Document';
        } else if (title.toLowerCase().includes('service')) {
          return 'Service Document';
        } else if (title.toLowerCase().includes('consulting')) {
          return 'Consulting Document';
        } else if (title.toLowerCase().includes('confidential') || title.toLowerCase().includes('non-disclosure')) {
          return 'Confidentiality Document';
        } else if (title.toLowerCase().includes('rental') || title.toLowerCase().includes('lease')) {
          return 'Rental Document';
        } else if (title.toLowerCase().includes('sale') || title.toLowerCase().includes('purchase')) {
          return 'Sales Document';
        } else if (title.toLowerCase().includes('software') || title.toLowerCase().includes('licensing')) {
          return 'Software Document';
        } else {
          return `Legal ${type}`;
        }
      }
    }
    
    // If no specific type is found, provide a generic label
    return 'Legal Document';
  };
  
  useEffect(() => {
    // Check if there's extracted text in localStorage
    const extractedText = localStorage.getItem('extractedDocumentText');
    console.log(`DocumentEditorComponent: Checking for extracted text in localStorage: ${extractedText ? 'Found' : 'Not found'}`);
    
    if (extractedText && document.sections.length === 0) {
      console.log(`DocumentEditorComponent: Found extracted text (${extractedText.length} characters) and no sections exist`);
      
      // Parse the extracted text into sections
      const parsedSections = parseExtractedText(extractedText);
      console.log(`DocumentEditorComponent: Parsed ${parsedSections.length} sections from extracted text`);
      
      // Update the sections state with the extracted sections
      setSections(parsedSections);
      console.log('DocumentEditorComponent: Updated sections with parsed content');
      
      // Clean up localStorage
      localStorage.removeItem('extractedDocumentText');
      console.log('DocumentEditorComponent: Cleaned up localStorage');
    } else {
      console.log(`DocumentEditorComponent: Using existing document sections (${document.sections.length})`);
    }
  }, [document]);
  
  // Helper function to parse extracted text into sections
  const parseExtractedText = (text: string): DocumentSection[] => {
    // Check if this is the Welocalize NDA based on content
    if (text.includes('WELOCALIZE, INC.') && text.includes('MUTUAL NONDISCLOSURE AGREEMENT')) {
      console.log('Detected Welocalize NDA format');
      return parseWelocalizeMutualNDA(text);
    }

    // Standard section detection based on common headers in legal documents
    const sectionRegex = /\n\s*(SECTION|Article|ARTICLE|Section|\d+\.|[IVX]+\.|[A-Z][A-Z\s]+:)\s*([^\n]+)/g;
    
    // Find all matches
    const matches: Array<{index: number, title: string, fullMatch: string}> = [];
    let match;
    
    // Reset regex before using it
    sectionRegex.lastIndex = 0;
    
    // Find all matches manually
    while ((match = sectionRegex.exec(text)) !== null) {
      matches.push({
        index: match.index,
        title: match[2].trim(),
        fullMatch: match[0]
      });
    }
    
    if (matches.length === 0) {
      // If no sections detected, create a simple structure
      return [
        { id: 1, title: 'Document', content: text }
      ];
    }
    
    const sections: DocumentSection[] = [];
    let lastIndex = 0;
    
    for (let i = 0; i < matches.length; i++) {
      const match = matches[i];
      const startIndex = match.index;
      const sectionTitle = match.title;
      
      // For the first section, include any content before it as an introduction
      if (i === 0 && startIndex > 0) {
        sections.push({
          id: sections.length + 1,
          title: 'Introduction',
          content: text.substring(0, startIndex).trim()
        });
      }
      
      // Calculate where this section ends (start of next section or end of content)
      const endIndex = i < matches.length - 1 ? 
        matches[i+1].index : 
        text.length;
      
      // Extract section content, skipping the header itself
      const headerLength = match.fullMatch.length;
      const sectionContent = text.substring(startIndex + headerLength, endIndex).trim();
      
      sections.push({
        id: sections.length + 1,
        title: sectionTitle,
        content: sectionContent
      });
      
      lastIndex = endIndex;
    }
    
    // If there's content after the last section, add it as a conclusion
    if (lastIndex < text.length - 1) {
      sections.push({
        id: sections.length + 1,
        title: 'Conclusion',
        content: text.substring(lastIndex).trim()
      });
    }
    
    return sections;
  };

  // Special parser for Welocalize Mutual NDA format
  const parseWelocalizeMutualNDA = (text: string): DocumentSection[] => {
    const sections: DocumentSection[] = [];
    
    // Extract the header/introduction
    const introEndIndex = text.indexOf('1. Purpose.');
    if (introEndIndex > 0) {
      const introText = text.substring(0, introEndIndex).trim();
      sections.push({
        id: 1,
        title: 'Introduction',
        content: introText
      });
    }
    
    // Define regex patterns for numbered sections in the NDA
    const sectionRegex = /(\d+)\.\s+([^.\n]+)\.(?:\s*\n|\s+)([^]*?)(?=\n\d+\.\s+|$)/g;
    let match;
    
    while ((match = sectionRegex.exec(text)) !== null) {
      const sectionNumber = match[1];
      const sectionTitle = match[2].trim();
      const sectionContent = match[3].trim();
      
      sections.push({
        id: parseInt(sectionNumber) + 1, // +1 because we already have the intro
        title: `${sectionNumber}. ${sectionTitle}`,
        content: sectionContent
      });
    }
    
    // Extract the signature section if present
    const signatureIndex = text.indexOf('ACKNOWLEDGED AND AGREED:');
    if (signatureIndex > 0) {
      const signatureText = text.substring(signatureIndex).trim();
      sections.push({
        id: sections.length + 1,
        title: 'Signatures',
        content: signatureText
      });
    }
    
    return sections;
  };
  
  return (
    <Box className="document-editor" sx={{ position: 'relative' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <TextField
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            variant="standard"
            placeholder="Document Title"
            fullWidth
            sx={{ 
              '& .MuiInputBase-input': {
                fontSize: '1.75rem',
                fontWeight: 700,
                color: 'primary.main',
                '&:focus': {
                  color: 'primary.dark',
                },
              },
              '& .MuiInput-underline:before': { borderBottomColor: 'transparent' },
              '& .MuiInput-underline:hover:before': { borderBottomColor: 'rgba(0, 0, 0, 0.42)' },
            }}
          />
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
          
          <IconButton onClick={handleMenuOpen}>
            <MoreIcon />
          </IconButton>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handlePrintDocument}>
              <ListItemIcon>
                <PrintIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Print Document</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleDownloadPDF}>
              <ListItemIcon>
                <DownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download as PDF</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleShareDocument}>
              <ListItemIcon>
                <ShareIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Share Document</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleViewHistory}>
              <ListItemIcon>
                <HistoryIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>View History</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>
      
      {/* PDF Notification Banner */}
      <PdfNotificationBanner />
      
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Edit" />
          <Tab label="Preview" />
        </Tabs>
      </Box>
      
      {/* Document editor tabs */}
      <Box sx={{ borderBottom: '1px solid rgba(58, 54, 224, 0.1)', mb: 4 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            '& .MuiTab-root': {
              fontWeight: 600,
              fontSize: '0.95rem',
              textTransform: 'none',
              py: 2,
              px: 3,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                color: 'primary.main',
                opacity: 0.8
              }
            },
            '& .Mui-selected': {
              color: 'primary.main',
              fontWeight: 700
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: 1.5
            }
          }}
        >
          <Tab label="Editor" icon={<EditIcon sx={{ fontSize: 20 }} />} iconPosition="start" />
          <Tab label="Preview" icon={<DescriptionIcon sx={{ fontSize: 20 }} />} iconPosition="start" />
          <Tab label="History" icon={<HistoryIcon sx={{ fontSize: 20 }} />} iconPosition="start" />
        </Tabs>
      </Box>
      
      {/* Editor tab content - Document-like editing interface */}
      <Box hidden={activeTab !== 0}>
        <Paper 
                  sx={{ 
            p: { xs: 3, md: 6 }, 
            minHeight: '60vh',
            maxWidth: '850px',
            mx: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 3,
            bgcolor: '#fff'
                  }}
                >
          {/* Document content container */}
          <Box>
            {/* Document Header */}
            <Box sx={{ mb: 4 }}>
              <TextField
                fullWidth
                variant="standard"
                value={document.title}
                onChange={(e) => onTitleChange?.(e.target.value)}
                InputProps={{
                  style: {
                    fontSize: '2rem',
                    fontWeight: 700,
                    color: '#1a365d',
                    fontFamily: document.title.toLowerCase().includes('welocalize') ? 
                      '"Arial", "Helvetica", sans-serif' : 
                      '"Merriweather", "Times New Roman", serif',
                  },
                  disableUnderline: true
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    textAlign: document.title.toLowerCase().includes('welocalize') ? 'center' : 'left',
                  }
                }}
              />
              </Box>
              
            {/* Document Sections */}
            {sections.map((section, index) => (
                    <Box 
                      key={section.id}
                className="document-page"
                      sx={{ 
                        position: 'relative',
                  mb: 6,
                  pb: 6,
                  borderBottom: index < sections.length - 1 ? '1px dashed #ccc' : 'none',
                  '&:hover .section-controls': {
                    opacity: 1
                  }
                }}
                    >
                {/* Section Controls */}
                <Box 
                  className="section-controls"
                  sx={{ 
                        position: 'absolute',
                    right: -60,
                        top: 0,
                        display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    '@media (max-width: 1200px)': {
                      right: 0,
                      top: -40,
                      flexDirection: 'row'
                    }
                  }}
                >
                  <Tooltip title="Move section up">
                    <IconButton 
                            size="small"
                      onClick={() => handleMoveSectionUp(index)}
                      disabled={index === 0}
                            sx={{ 
                        bgcolor: 'white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.08)' }
                      }}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Move section down">
                        <IconButton 
                          size="small"
                      onClick={() => handleMoveSectionDown(index)}
                      disabled={index === sections.length - 1}
                          sx={{ 
                        bgcolor: 'white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.08)' }
                          }}
                        >
                      <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete section">
                    <IconButton 
                  size="small"
                      onClick={() => onRemoveSection(index)}
                  sx={{ 
                        bgcolor: 'white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.08)' },
                        color: 'error.main'
                  }}
                >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="AI suggestions">
                    <IconButton 
                    size="small"
                      onClick={() => handleAIDrawerToggle()}
                    sx={{ 
                        bgcolor: 'white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.08)' }
                    }}
                  >
                      <AIIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
              </Box>

                {/* Section Header */}
                  <Box sx={{ 
                    display: 'flex', 
                  alignItems: 'flex-start',
                  mb: 2
                  }}>
                  {!title.toLowerCase().includes('welocalize') && (
                      <Box sx={{ 
                      minWidth: 32,
                        height: 32, 
                        borderRadius: '50%', 
                      bgcolor: '#1a365d',
                      color: 'white',
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                      fontWeight: 700,
                      mr: 2,
                      mt: 0.5
                      }}>
                      {index + 1}
                      </Box>
                  )}
                  <TextField
                    fullWidth
                    variant="standard"
                    value={section.title}
                    onChange={(e) => onSectionTitleChange(index, e.target.value)}
                    onBlur={(e) => handleSectionTitleResize(e, index)}
                    InputProps={{
                      style: {
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        color: '#1a365d',
                        fontFamily: title.toLowerCase().includes('welocalize') ? 
                          '"Arial", "Helvetica", sans-serif' : 
                          '"Merriweather", "Times New Roman", serif',
                      },
                      disableUnderline: true
                    }}
                    sx={{
                      borderBottom: '1px solid #e0e0e0',
                      pb: 1,
                      mb: 2,
                      '& .MuiInputBase-input': {
                        '&:hover, &:focus': {
                          backgroundColor: 'rgba(0,0,0,0.02)'
                        }
                      }
                    }}
                  />
                  </Box>
                  
                {/* Section Content */}
                <Box sx={{ position: 'relative' }}>
                  <TextField
                    multiline
                    fullWidth
                    value={section.content}
                    onChange={(e) => onSectionContentChange(e.target.value)}
                    variant="standard"
                    placeholder="Enter section content..."
                    InputProps={{
                      disableUnderline: true,
                      style: {
                        fontFamily: title.toLowerCase().includes('welocalize') ? 
                          '"Arial", "Helvetica", sans-serif' : 
                          '"Merriweather", "Times New Roman", serif',
                        fontSize: '0.95rem',
                        lineHeight: 1.8,
                        color: '#333',
                        textAlign: 'justify'
                        }
                    }}
                    sx={{
                      pl: title.toLowerCase().includes('welocalize') ? 0 : 6,
                      '& .MuiInputBase-input': {
                        '&:hover, &:focus': {
                          backgroundColor: 'rgba(0,0,0,0.02)'
                        }
                      }
                    }}
                  />

                  {/* Editing tip */}
                  <Tooltip 
                    title={getEditingTipForSection(section.title)}
                    placement="left"
                    sx={{ 
                      position: 'absolute',
                      right: -40,
                      top: 0
                    }}
                  >
                    <IconButton size="small">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Welocalize footer */}
                {title.toLowerCase().includes('welocalize') && (
                  <Box sx={{ 
                    mt: 'auto',
                    pt: 2,
                    borderTop: '1px solid #ff6600',
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    bgcolor: '#ff6600',
                    color: 'white',
                    p: 1,
                    borderRadius: '0 0 4px 4px'
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                      {index + 1} | {sections.length} © 2024, WELOCALIZE, INC. • CONFIDENTIAL & PROPRIETARY • 2406
                    </Typography>
                    <Box 
                      component="img"
                      src="https://www.welocalize.com/wp-content/uploads/2020/01/welocalize-logo-white.svg"
                      alt="Welocalize"
                      sx={{ 
                        height: 20,
                        filter: 'brightness(0) invert(1)'
                      }}
                    />
                  </Box>
                )}

                {/* Page number */}
                <Box sx={{ 
                  position: 'absolute',
                  bottom: 20,
                  right: 20,
                  fontSize: '0.75rem',
                  color: '#666'
                  }}>
                  Page {index + 1} of {sections.length}
                </Box>
              </Box>
            ))}

            {/* Add Section Button */}
            <Box sx={{ textAlign: 'center', mt: 4 }}>
                      <Button
                        variant="outlined"
                        color="primary"
                startIcon={<AddIcon />}
                onClick={onAddSection}
                sx={{ 
                  borderStyle: 'dashed',
                  '&:hover': {
                    borderStyle: 'dashed'
                          }
                        }}
              >
                Add New Section
                      </Button>
            </Box>

            {/* Document Footer with Signature Lines */}
            {sections.length > 0 && (
              <Box 
                sx={{ 
                  mt: 8, 
                  pt: 4, 
                  borderTop: '1px solid #e0e0e0',
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  justifyContent: 'space-between',
                  gap: 4
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ mb: 3, color: '#666', fontWeight: 500 }}>
                    {title.toLowerCase().includes('welocalize') ? 'Welocalize, Inc.' : 'Party 1:'}
                  </Typography>
                  <Box sx={{ borderBottom: '1px solid #000', width: '100%', mb: 1 }}></Box>
                  <Typography variant="caption" sx={{ color: '#666' }}>Signature</Typography>
                  <Box sx={{ borderBottom: '1px solid #000', width: '100%', mt: 3, mb: 1 }}></Box>
                  <Typography variant="caption" sx={{ color: '#666' }}>Date</Typography>
                    </Box>
                
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ mb: 3, color: '#666', fontWeight: 500 }}>
                    {title.toLowerCase().includes('welocalize') ? 'Supplier' : 'Party 2:'}
                  </Typography>
                  <Box sx={{ borderBottom: '1px solid #000', width: '100%', mb: 1 }}></Box>
                  <Typography variant="caption" sx={{ color: '#666' }}>Signature</Typography>
                  <Box sx={{ borderBottom: '1px solid #000', width: '100%', mt: 3, mb: 1 }}></Box>
                  <Typography variant="caption" sx={{ color: '#666' }}>Date</Typography>
                </Box>
                </Box>
              )}
          </Box>

          {/* Document Watermark */}
          <Box 
            sx={{ 
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%) rotate(-45deg)',
              opacity: 0.03,
              pointerEvents: 'none',
              fontSize: '8rem',
              fontWeight: 900,
              color: '#000',
              whiteSpace: 'nowrap'
            }}
          >
            DRAFT COPY
          </Box>
            </Paper>
      </Box>
      
      {/* Preview tab content - Professional document styling for printing */}
      <Box hidden={activeTab !== 1}>
        <Paper 
          sx={{ 
            p: { xs: 3, md: 6 }, 
            minHeight: '60vh',
            maxWidth: '850px',
            mx: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 3,
            bgcolor: '#fff',
            '@media print': {
              boxShadow: 'none',
              p: 0,
              m: 0,
              width: '100%'
            }
          }}
        >
          {/* Document Preview Controls - Hidden in print */}
          <Box sx={{ 
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 10,
            display: 'flex',
            gap: 1,
            '@media print': { display: 'none' }
          }}>
            <Tooltip title="Print document">
              <IconButton 
                color="primary"
                onClick={handlePrintDocument}
                sx={{ 
                  bgcolor: 'white', 
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                  '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.08)' }
                }}
              >
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download as PDF">
              <IconButton 
                color="primary"
                onClick={handleDownloadPDF}
                sx={{ 
                  bgcolor: 'white', 
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                  '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.08)' }
                }}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Document content container - This wrapper helps with page breaks */}
          <Box sx={{ 
            '@media print': { 
              display: 'flex', 
              flexDirection: 'column'
            }
          }}>
            {/* Document Envelope ID */}
            {title.toLowerCase().includes('welocalize') && (
              <Box sx={{ 
                mb: 3, 
                fontSize: '0.75rem',
                color: '#666'
              }}>
                Docusign Envelope ID: CBBEE242-B7AE-49FE-AF19-1207E951D571
              </Box>
            )}
            
          {/* Document Header with Logo and Title */}
          <Box 
            sx={{ 
              mb: 5, 
              pb: 3, 
                borderBottom: title.toLowerCase().includes('welocalize') ? 'none' : '2px solid #1a365d',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'flex-end' }
            }}
          >
              <Box sx={{ textAlign: title.toLowerCase().includes('welocalize') ? 'center' : 'left', width: '100%' }}>
              <Typography 
                variant="h4" 
                sx={{ 
                    fontFamily: title.toLowerCase().includes('welocalize') ? 
                      '"Arial", "Helvetica", sans-serif' : 
                      '"Merriweather", "Times New Roman", serif',
                  fontWeight: 700,
                  color: '#1a365d',
                  mb: 1,
                  letterSpacing: '0.5px',
                    wordBreak: 'break-word',
                    textAlign: title.toLowerCase().includes('welocalize') ? 'center' : 'left',
                    textTransform: title.toLowerCase().includes('welocalize') ? 'uppercase' : 'none',
                }}
              >
                {title || "Untitled Document"}
              </Typography>
                
                {title.toLowerCase().includes('mutual nondisclosure agreement') && (
              <Typography 
                    variant="h5" 
                sx={{ 
                      fontFamily: '"Arial", "Helvetica", sans-serif',
                  fontWeight: 700,
                      color: '#ff6600',
                      mb: 3,
                      letterSpacing: '0.5px',
                      textAlign: 'center',
                  textTransform: 'uppercase',
                }}
              >
                    MUTUAL NONDISCLOSURE AGREEMENT
              </Typography>
                )}
          
                {!title.toLowerCase().includes('welocalize') && (
                  <Typography 
                    variant="subtitle1" 
            sx={{ 
                      color: '#666', 
                      fontStyle: 'italic',
                      fontFamily: '"Merriweather", "Times New Roman", serif',
                    }}
                  >
                  {new Date().toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </Typography>
                )}
            </Box>
          </Box>
          
            {/* Document Content with Professional Styling - Each section gets its own "page" */}
            {sections.length > 0 ? (
              sections.map((section, index) => (
                <Box 
                  key={section.id} 
                  className="document-page"
                  sx={{ 
                    position: 'relative',
                    pageBreakBefore: index > 0 ? 'always' : 'auto',
                    pageBreakAfter: 'always',
                    pageBreakInside: 'avoid',
                    minHeight: index < sections.length - 1 ? '90vh' : 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    '@media screen': {
                      mb: 6,
                      pb: 6,
                      borderBottom: index < sections.length - 1 ? '1px dashed #ccc' : 'none'
                    }
                  }}
                >
                  {/* Section header */}
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    mb: 2
                  }}>
                    {!title.toLowerCase().includes('welocalize') && (
                    <Box sx={{ 
                      minWidth: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: '#1a365d',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      mr: 2,
                      mt: 0.5
                    }}>
                      {index + 1}
                    </Box>
                    )}
                    <Typography 
                      variant="h6" 
                      gutterBottom
                      sx={{ 
                        fontFamily: title.toLowerCase().includes('welocalize') ? 
                          '"Arial", "Helvetica", sans-serif' : 
                          '"Merriweather", "Times New Roman", serif',
                        fontWeight: 700,
                        color: '#1a365d',
                        borderBottom: '1px solid #e0e0e0',
                        pb: 1,
                        mb: 2,
                        wordBreak: 'break-word',
                        width: '100%'
                      }}
                    >
                      {section.title || `Section ${index + 1}`}
                    </Typography>
                  </Box>
                  
                  {/* Section content */}
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        whiteSpace: 'pre-wrap',
                        fontFamily: title.toLowerCase().includes('welocalize') ? 
                          '"Arial", "Helvetica", sans-serif' : 
                          '"Merriweather", "Times New Roman", serif',
                        fontSize: '0.95rem',
                        lineHeight: 1.8,
                        color: '#333',
                        textAlign: 'justify',
                        hyphens: 'auto',
                        pl: title.toLowerCase().includes('welocalize') ? 0 : 6
                      }}
                    >
                      {section.content}
                    </Typography>
                  </Box>
                  
                  {/* Welocalize footer */}
                  {title.toLowerCase().includes('welocalize') && (
                    <Box sx={{ 
                      mt: 'auto',
                      pt: 2,
                      borderTop: '1px solid #ff6600',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      bgcolor: '#ff6600',
                      color: 'white',
                      p: 1,
                      borderRadius: '0 0 4px 4px'
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        {index + 1} | {sections.length} © 2024, WELOCALIZE, INC. • CONFIDENTIAL & PROPRIETARY • 2406
                      </Typography>
                      <Box 
                        component="img"
                        src="https://www.welocalize.com/wp-content/uploads/2020/01/welocalize-logo-white.svg"
                        alt="Welocalize"
                      sx={{ 
                          height: 20,
                          filter: 'brightness(0) invert(1)'
                        }}
                      />
                    </Box>
                  )}
                  
                  {/* Page number */}
                  <Box sx={{ 
                    position: 'absolute',
                    bottom: 20,
                    right: 20,
                    fontSize: '0.75rem',
                    color: '#666',
                    '@media print': {
                      bottom: 10
                    }
                  }}>
                    Page {index + 1} of {sections.length}
                  </Box>
                </Box>
              ))
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" sx={{ mb: 2, fontStyle: 'italic', color: '#666' }}>
                  This document has no sections yet.
                </Typography>
              </Box>
            )}
          
          {/* Document Footer with Signature Lines */}
            {sections.length > 0 && (
          <Box 
            sx={{ 
              mt: 8, 
              pt: 4, 
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
                  gap: 4,
                  pageBreakBefore: 'always',
                  pageBreakInside: 'avoid'
            }}
          >
            <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ mb: 3, color: '#666', fontWeight: 500 }}>
                    {title.toLowerCase().includes('welocalize') ? 'Welocalize, Inc.' : 'Party 1:'}
                  </Typography>
              <Box sx={{ borderBottom: '1px solid #000', width: '100%', mb: 1 }}></Box>
              <Typography variant="caption" sx={{ color: '#666' }}>Signature</Typography>
              <Box sx={{ borderBottom: '1px solid #000', width: '100%', mt: 3, mb: 1 }}></Box>
              <Typography variant="caption" sx={{ color: '#666' }}>Date</Typography>
            </Box>
            
            <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ mb: 3, color: '#666', fontWeight: 500 }}>
                    {title.toLowerCase().includes('welocalize') ? 'Supplier' : 'Party 2:'}
                  </Typography>
              <Box sx={{ borderBottom: '1px solid #000', width: '100%', mb: 1 }}></Box>
              <Typography variant="caption" sx={{ color: '#666' }}>Signature</Typography>
              <Box sx={{ borderBottom: '1px solid #000', width: '100%', mt: 3, mb: 1 }}></Box>
              <Typography variant="caption" sx={{ color: '#666' }}>Date</Typography>
            </Box>
          </Box>
            )}
          </Box>
        </Paper>
      </Box>
      
      {/* History tab content */}
      <Box hidden={activeTab !== 2}>
        <Paper sx={{ p: 3, minHeight: '60vh' }}>
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mt: 10 }}>
            Document history will be displayed here
          </Typography>
        </Paper>
      </Box>
      
      {/* AI Assistant Drawer */}
      <Drawer
        anchor="right"
        open={aiDrawerOpen}
        onClose={handleAIDrawerToggle}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 450 },
            p: 4,
            boxSizing: 'border-box',
            borderRadius: '20px 0 0 20px',
            boxShadow: '-10px 0 30px rgba(58, 54, 224, 0.15)',
            background: 'linear-gradient(145deg, #ffffff, #f8f9ff)',
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography 
            variant="h5"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <AIIcon sx={{ fontSize: 28 }} />
            AI Assistant
          </Typography>
          <IconButton 
            onClick={handleAIDrawerToggle}
            sx={{
              bgcolor: 'rgba(58, 54, 224, 0.05)',
              '&:hover': {
                bgcolor: 'rgba(58, 54, 224, 0.1)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Divider sx={{ mb: 4, borderColor: 'rgba(58, 54, 224, 0.1)' }} />
        
        <Typography 
          variant="subtitle1" 
          gutterBottom
          sx={{ 
            fontWeight: 600, 
            color: 'text.primary',
            mb: 2 
          }}
        >
          What would you like the AI to do?
        </Typography>
        
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder="E.g., 'Rewrite this section to be more concise' or 'Add a clause about data privacy'"
          value={aiPrompt}
          onChange={(e) => setAIPrompt(e.target.value)}
          variant="outlined"
          sx={{ 
            mb: 3,
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              },
              '&.Mui-focused': {
                boxShadow: '0 4px 20px rgba(58, 54, 224, 0.15)'
              }
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: '1px solid rgba(58, 54, 224, 0.1)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }
          }}
        />
        
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleAIPromptSubmit}
          disabled={!aiPrompt.trim() || aiLoading}
          startIcon={aiLoading ? <CircularProgress size={20} color="inherit" /> : <PsychologyIcon />}
          sx={{ 
            mb: 4,
            py: 1.5,
            borderRadius: 3,
            background: 'linear-gradient(45deg, #3a36e0 30%, #6e6ff7 90%)',
            boxShadow: '0 8px 20px rgba(58, 54, 224, 0.25)',
            '&:hover': {
              boxShadow: '0 12px 28px rgba(58, 54, 224, 0.35)',
              transform: 'translateY(-2px)'
            },
            '&.Mui-disabled': {
              background: 'rgba(58, 54, 224, 0.12)',
              color: 'rgba(255, 255, 255, 0.6)'
            }
          }}
        >
          {aiLoading ? 'Generating...' : 'Generate with AI'}
        </Button>
        
        {aiResult && (
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="subtitle1" 
              gutterBottom
              sx={{ 
                fontWeight: 600, 
                color: 'primary.main',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <AutoAwesomeIcon fontSize="small" />
              AI Generated Result
            </Typography>
            
            <Paper
              elevation={0}
              sx={{ 
                p: 3, 
                mb: 3, 
                maxHeight: 300, 
                overflow: 'auto',
                borderRadius: 3,
                backgroundColor: 'rgba(58, 54, 224, 0.03)',
                border: '1px solid rgba(58, 54, 224, 0.1)',
                position: 'relative'
              }}
            >
              <Typography 
                variant="body1" 
                sx={{ 
                  whiteSpace: 'pre-wrap',
                  color: 'text.primary',
                  lineHeight: 1.7
                }}
              >
                {aiResult}
              </Typography>
            </Paper>
            
            <Button
              fullWidth
              variant="contained"
              color="secondary"
              onClick={handleApplyAIResult}
              startIcon={<CheckIcon />}
              sx={{ 
                py: 1.5,
                borderRadius: 3,
                boxShadow: '0 8px 20px rgba(255, 90, 95, 0.25)',
                '&:hover': {
                  boxShadow: '0 12px 28px rgba(255, 90, 95, 0.35)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              Apply to Document
            </Button>
          </Box>
        )}
        
        <Divider sx={{ my: 4, borderColor: 'rgba(58, 54, 224, 0.1)' }} />
        
        <Typography 
          variant="subtitle1" 
          gutterBottom
          sx={{ 
            fontWeight: 600, 
            color: 'text.primary',
            mb: 2 
          }}
        >
          Quick Actions
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<TextFieldsIcon />}
            onClick={() => {
              setAIPrompt('Improve the clarity of this section');
              // Automatically submit the prompt if a section is selected
              if (activeSection !== null && onAIRequest) {
                setTimeout(() => {
                  handleAIPromptSubmit();
                }, 100);
              }
            }}
            disabled={activeSection === null}
            sx={{ 
              justifyContent: 'flex-start',
              py: 1.5,
              px: 2,
              borderRadius: 3,
              borderWidth: '1px',
              borderColor: 'rgba(58, 54, 224, 0.3)',
              backgroundColor: activeSection === null ? 'rgba(0, 0, 0, 0.05)' : 'rgba(58, 54, 224, 0.03)',
              '&:hover': {
                borderWidth: '1px',
                backgroundColor: 'rgba(58, 54, 224, 0.08)',
                transform: activeSection === null ? 'none' : 'translateY(-2px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
              <Typography variant="body1" fontWeight={600}>Improve Clarity</Typography>
              {activeSection === null && (
                <Typography variant="caption" color="text.secondary">Select a section first</Typography>
              )}
            </Box>
          </Button>
          
          <Button
            variant="outlined"
            color="primary"
            startIcon={<TranslateIcon />}
            onClick={() => {
              setAIPrompt('Simplify the language in this section');
              // Automatically submit the prompt if a section is selected
              if (activeSection !== null && onAIRequest) {
                setTimeout(() => {
                  handleAIPromptSubmit();
                }, 100);
              }
            }}
            disabled={activeSection === null}
            sx={{ 
              justifyContent: 'flex-start',
              py: 1.5,
              px: 2,
              borderRadius: 3,
              borderWidth: '1px',
              borderColor: 'rgba(58, 54, 224, 0.3)',
              backgroundColor: activeSection === null ? 'rgba(0, 0, 0, 0.05)' : 'rgba(58, 54, 224, 0.03)',
              '&:hover': {
                borderWidth: '1px',
                backgroundColor: 'rgba(58, 54, 224, 0.08)',
                transform: activeSection === null ? 'none' : 'translateY(-2px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
              <Typography variant="body1" fontWeight={600}>Simplify Language</Typography>
              {activeSection === null && (
                <Typography variant="caption" color="text.secondary">Select a section first</Typography>
              )}
            </Box>
          </Button>
          
          <Button 
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
              setAIPrompt('Add a clause to protect against liability');
              // Automatically submit the prompt if a section is selected
              if (activeSection !== null && onAIRequest) {
                setTimeout(() => {
                  handleAIPromptSubmit();
                }, 100);
              }
            }}
            disabled={activeSection === null}
            sx={{ 
              justifyContent: 'flex-start',
              py: 1.5,
              px: 2,
              borderRadius: 3,
              borderWidth: '1px',
              borderColor: 'rgba(58, 54, 224, 0.3)',
              backgroundColor: activeSection === null ? 'rgba(0, 0, 0, 0.05)' : 'rgba(58, 54, 224, 0.03)',
              '&:hover': {
                borderWidth: '1px',
                backgroundColor: 'rgba(58, 54, 224, 0.08)',
                transform: activeSection === null ? 'none' : 'translateY(-2px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
              <Typography variant="body1" fontWeight={600}>Add Liability Protection</Typography>
              {activeSection === null && (
                <Typography variant="caption" color="text.secondary">Select a section first</Typography>
              )}
            </Box>
          </Button>
          
          <Button 
            variant="outlined"
            color="primary"
            startIcon={<SummarizeIcon />}
            onClick={() => {
              setAIPrompt('Summarize this section');
              // Automatically submit the prompt if a section is selected
              if (activeSection !== null && onAIRequest) {
                setTimeout(() => {
                  handleAIPromptSubmit();
                }, 100);
              }
            }}
            disabled={activeSection === null}
            sx={{ 
              justifyContent: 'flex-start',
              py: 1.5,
              px: 2,
              borderRadius: 3,
              borderWidth: '1px',
              borderColor: 'rgba(58, 54, 224, 0.3)',
              backgroundColor: activeSection === null ? 'rgba(0, 0, 0, 0.05)' : 'rgba(58, 54, 224, 0.03)',
              '&:hover': {
                borderWidth: '1px',
                backgroundColor: 'rgba(58, 54, 224, 0.08)',
                transform: activeSection === null ? 'none' : 'translateY(-2px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
              <Typography variant="body1" fontWeight={600}>Summarize</Typography>
              {activeSection === null && (
                <Typography variant="caption" color="text.secondary">Select a section first</Typography>
              )}
            </Box>
          </Button>
        </Box>
      </Drawer>
      
      {/* Risk Analysis Dialog */}
      {riskAnalysis.length > 0 && (
        <Dialog
          open={riskAnalysis.length > 0}
          onClose={() => setRiskAnalysis([])}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              boxShadow: '0 20px 60px rgba(58, 54, 224, 0.15)',
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle sx={{ pb: 2, pt: 3, px: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  fontWeight: 700,
                  color: 'primary.main',
                  gap: 1.5
                }}
              >
                <WarningIcon sx={{ color: 'warning.main', fontSize: 28 }} />
                Risk Analysis Results
              </Typography>
              <IconButton
                onClick={() => setRiskAnalysis([])}
                sx={{ 
                  bgcolor: 'rgba(58, 54, 224, 0.05)',
                  '&:hover': {
                    bgcolor: 'rgba(58, 54, 224, 0.1)',
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          
          <DialogContent sx={{ px: 4, py: 3, borderTop: '1px solid rgba(58, 54, 224, 0.1)' }}>
            <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
              The following potential risks and issues were identified in your document. Review these carefully to improve your contract's clarity and legal protection.
            </Typography>
            
            <Grid container spacing={3}>
              {riskAnalysis.map((risk, index) => (
                <Grid item xs={12} key={index}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      borderRadius: 3, 
                      overflow: 'hidden',
                      border: '1px solid rgba(58, 54, 224, 0.08)',
                      boxShadow: '0 8px 20px rgba(58, 54, 224, 0.08)',
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 6,
                        backgroundColor: risk.severity === 'high' ? 'error.main' : risk.severity === 'medium' ? 'warning.main' : 'info.main'
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: '0 12px 30px rgba(58, 54, 224, 0.12)'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
                        <Typography variant="h6" fontWeight="700" sx={{ color: 'text.primary' }}>
                          {risk.title || (risk.section ? `Issue in ${risk.section}` : 'Document Issue')}
                        </Typography>
                        <Chip
                          label={risk.severity?.toUpperCase() || 'ISSUE'}
                          color={risk.severity === 'high' ? 'error' : risk.severity === 'medium' ? 'warning' : 'info'}
                          size="small"
                          sx={{ fontWeight: 700, borderRadius: 3, px: 1 }}
                        />
                      </Box>
                      
                      <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                        {risk.content || risk.issue}
                      </Typography>
                      
                      {risk.suggestion && (
                        <Box sx={{ mt: 1, p: 1.5, backgroundColor: '#f0f7ff', borderRadius: '4px' }}>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                            <TipIcon sx={{ mr: 1, color: '#0288d1', fontSize: '1rem' }} />
                            <strong>Recommendation:</strong> {risk.suggestion}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">
              Analysis generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
            </Typography>
            <Box>
              <Button 
                onClick={() => setRiskAnalysis([])} 
                color="primary"
                variant="outlined"
                sx={{ mr: 1 }}
              >
                Close
              </Button>
              <Button 
                onClick={() => {
                  // Apply all risk analysis suggestions to the document
                  let updatedSections = [...sections];
                  let improvementsApplied = 0;
                  
                  // For each risk that has a suggestion
                  riskAnalysis.forEach(risk => {
                    // Try to find the matching section based on section name or title
                    let sectionIndex = -1;
                    
                    if (risk.section) {
                      // Find by section name
                      sectionIndex = updatedSections.findIndex(
                        section => section.title.toLowerCase().includes(risk.section.toLowerCase())
                      );
                    } else if (risk.title) {
                      // Try to match by risk title if no section is specified
                      const titleWords = risk.title.toLowerCase().split(' ').filter((word: string) => word.length > 4);
                      
                      // Find a section that contains any of the significant words from the risk title
                      for (let i = 0; i < updatedSections.length; i++) {
                        const sectionContent = updatedSections[i].title.toLowerCase() + ' ' + updatedSections[i].content.toLowerCase();
                        if (titleWords.some((word: string) => sectionContent.includes(word))) {
                          sectionIndex = i;
                          break;
                        }
                      }
                    }
                    
                    // If no section found, use the first section as fallback
                    if (sectionIndex === -1 && updatedSections.length > 0) {
                      sectionIndex = 0;
                    }
                    
                    if (sectionIndex !== -1 && risk.suggestion) {
                      // Format the improvement with a clear header and the suggestion
                      const improvementText = `\n\n**IMPROVEMENT (${new Date().toLocaleDateString()}): **\n${risk.suggestion}`;
                      
                      // Append the suggestion as an improvement to the section
                      updatedSections[sectionIndex].content += improvementText;
                      improvementsApplied++;
                    }
                  });
                  
                  // Update the sections with the improvements
                  setSections(updatedSections);
                  setActiveTab(0); // Switch to editor tab to show changes
                  setRiskAnalysis([]);
                  
                  if (improvementsApplied > 0) {
                    showSnackbar(`Applied ${improvementsApplied} risk improvements to document`, 'success');
                  } else {
                    showSnackbar('No improvements could be applied to the document', 'info');
                  }
                }} 
                color="primary"
                variant="contained"
                startIcon={<CheckIcon />}
                sx={{ mr: 1 }}
              >
                Apply Suggestions
              </Button>
              <Button 
                onClick={() => {
                  setRiskAnalysis([]);
                  showSnackbar('Risk analysis report saved', 'success');
                }} 
                color="primary"
                variant="contained"
                startIcon={<SaveIcon />}
              >
                Save Report
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      )}
      
      {/* Add ScrollToTop component */}
      <ScrollToTop />
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      {/* Section Context Menu */}
      <Menu
        anchorEl={sectionMenuAnchorEl}
        open={Boolean(sectionMenuAnchorEl)}
        onClose={handleSectionMenuClose}
      >
        <MenuItem 
          onClick={() => {
            if (sectionMenuIndex !== null) {
              handleMoveSectionUp(sectionMenuIndex);
              handleSectionMenuClose();
            }
          }}
          disabled={sectionMenuIndex === 0}
        >
          <ListItemIcon>
            <ArrowUpwardIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Move Up</Typography>
        </MenuItem>
        <MenuItem 
          onClick={() => {
            if (sectionMenuIndex !== null) {
              handleMoveSectionDown(sectionMenuIndex);
              handleSectionMenuClose();
            }
          }}
          disabled={sectionMenuIndex === sections.length - 1}
        >
          <ListItemIcon>
            <ArrowDownwardIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Move Down</Typography>
        </MenuItem>
        <MenuItem 
          onClick={() => {
            if (sectionMenuIndex !== null) {
              handleDeleteSection(sectionMenuIndex);
              handleSectionMenuClose();
            }
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Delete Section</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default DocumentEditorComponent;
